import { Schedule } from "@aura-frontend/omnibus-az-client";

/**
 * A schedule is considered deleted when both its internal and external payloads
 * have been cleared. We still receive these records from the API; consumers
 * filter them out.
 */
export const isDeletedSchedule = (schedule: Schedule): boolean =>
  !schedule.internal && !schedule.external;

/**
 * Externally-sourced schedules (e.g. from a third-party scheduling system)
 * cannot be deleted from this app.
 */
export const canDeleteSchedule = (schedule: Schedule): boolean =>
  !schedule.external;

/**
 * Omnibus sets a schedule's `result` once it has run (started its game, or
 * given up after its retries) and ignores edits to it from then on. The
 * generated client's type leaves `result` out.
 */
export const hasScheduleStarted = (schedule: Schedule): boolean =>
  (schedule as Schedule & { result?: unknown }).result != null;

/** The game a started schedule created, which omnibus stores on its game. */
export const getScheduleGameUuid = (schedule: Schedule): string | undefined =>
  schedule.internal?.game?.gameUuid ??
  schedule.merged?.game?.gameUuid ??
  undefined;

/**
 * The started schedule's game while it's still live: the field's current
 * game. Undefined for a schedule that hasn't started or whose game is over.
 */
export const getLiveScheduleGameUuid = (
  schedule: Schedule,
  currentGameUuid: string | null | undefined,
): string | undefined => {
  if (!hasScheduleStarted(schedule)) return undefined;
  const gameUuid = getScheduleGameUuid(schedule);
  return gameUuid && gameUuid === currentGameUuid ? gameUuid : undefined;
};

/**
 * The event menu's link to an event's details page, by the event id it opens.
 * A started event can't be edited: a live one opens its game's locked view
 * (as the field's active event card does), and an ended one has none.
 */
export const getScheduleDetailsLink = (
  schedule: Schedule,
  currentGameUuid: string | null | undefined,
): { label: "Edit Details" | "View Event"; eventId: string } | null => {
  if (!hasScheduleStarted(schedule)) {
    return { label: "Edit Details", eventId: schedule.id ?? "" };
  }
  const liveGameUuid = getLiveScheduleGameUuid(schedule, currentGameUuid);
  return liveGameUuid ? { label: "View Event", eventId: liveGameUuid } : null;
};
